import {
  ReportDailyResultImg,
  RiskLabel,
  WrappedSection,
} from "@/pages/common/report/WebReport2/commons";
import { SLEEP_JUDGE_TITLE_CONFIG } from "@/pages/common/report/WebReport2/SleepJudge/commons";
import { formatDisplayTime } from "@/utils/date";
import { isNotNullish } from "@/utils/objects";
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  NavigateNext,
} from "@mui/icons-material";
import {
  Breadcrumbs,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { format, parse } from "date-fns";
import Highcharts, { SeriesLegendItemClickEventObject } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useRef, useState } from "react";
import ReportNavigate from "./commons/ReportNavigate";
import { ScrollableContainer } from "./commons/ScrollableContainer";
import SubtitleTypography from "./commons/SubtitleTypography";
import { Quality2Response, UserType } from "./types";

export default function Quality2({
  uuid,
  userType,
  data,
}: {
  uuid: string | undefined;
  userType: UserType;
  data: Quality2Response;
}) {
  const [graphIndex, setGraphIndex] = useState(0);

  const GRAPH_INFOS = [
    {
      graph: (
        <SleepEfficiencyChart
          data={data.measurement_result.sleep_efficiencies}
        />
      ),
      label: "睡眠効率",
    },
    {
      graph: (
        <SOLandWASOChart
          data={data.measurement_result.sleep_onset_latency.map((x) => ({
            date: x.date,
            value: x.value != null ? x.value * 60 : null,
          }))}
          threshold={30}
        />
      ),
      label: "入眠潜時",
    },
    {
      graph: (
        <SOLandWASOChart
          data={data.measurement_result.waso.map((x) => ({
            date: x.date,
            value: x.value != null ? x.value / 60 : null,
          }))}
          threshold={60}
        />
      ),
      label: "中途覚醒時間",
    },
  ];
  const graphInfo = GRAPH_INFOS[graphIndex];

  const getGraphIndex = (index: number) =>
    ((index % GRAPH_INFOS.length) + GRAPH_INFOS.length) % GRAPH_INFOS.length;

  return (
    <Stack sx={{ px: 6 }}>
      <Stack sx={{ pt: 12, pb: 8, gap: 2 }}>
        <Breadcrumbs maxItems={2} separator={<NavigateNext fontSize="small" />}>
          <Link
            href={`/${userType}/report/${uuid}`}
            color="text.secondary"
            style={{ textDecoration: "none" }}
          >
            <Typography variant="body2">レポートTOP</Typography>
          </Link>
          <Typography variant="body2">睡眠の質</Typography>
        </Breadcrumbs>
        <Typography
          variant="h5"
          sx={{ textAlign: "justify", color: "primary.main" }}
        >
          睡眠の質
        </Typography>
      </Stack>
      <Divider />
      <Stack sx={{ pt: "49px", pb: 6 }}>
        <SubtitleTypography>日別の測定結果と判定</SubtitleTypography>
      </Stack>
      <Stack sx={{ pb: 12, gap: 4 }}>
        <Stack gap={4} sx={{ mb: 4 }}>
          <Stack sx={{ textAlign: "right" }}>
            <Typography
              variant="caption"
              sx={{ "&:before": { content: "'■'", color: "#00A99D", pr: 1 } }}
            >
              推奨範囲
            </Typography>
          </Stack>
          <Stack>{graphInfo.graph}</Stack>
          <Stack direction="row" justifyContent="space-between">
            <IconButton
              onClick={() =>
                setGraphIndex((graphIndex) => getGraphIndex(--graphIndex))
              }
            >
              <KeyboardArrowLeft />
            </IconButton>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ my: "auto" }}
            >
              表示グラフ：{graphInfo.label}
            </Typography>
            <IconButton
              onClick={() =>
                setGraphIndex((graphIndex) => getGraphIndex(++graphIndex))
              }
            >
              <KeyboardArrowRight />
            </IconButton>
          </Stack>
        </Stack>
        <QualityTable data={data} />
        <Stack gap={4} sx={{ mb: 4 }}>
          <Typography variant="subtitle1" color="text.secondary">
            日別判定の基準
          </Typography>
          <Stack gap={2}>
            <TableContainer
              sx={{
                borderRadius: 0,
                borderTop: "solid 1px #D8D7D6",
                borderRight: "solid 1px #D8D7D6",
                borderLeft: "solid 1px #D8D7D6",
                "& thead tr th, tr tbody td": {
                  whiteSpace: "nowrap",
                },
                "& tbody tr td": {
                  py: 4,
                  px: 2,
                },
                "& thead tr th": {
                  py: 2,
                  px: 0,
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="caption" sx={{ pl: 2 }}>
                        入眠潜時/中途覚醒時間
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderRight: "solid 1px #D8D7D6" }}>
                      <Typography
                        variant="caption"
                        sx={{ borderLeft: "solid 1px #D8D7D6", pl: 2 }}
                      >
                        睡眠効率
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="caption">判定</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      sx={{ borderRight: "dashed 1px #D8D7D6" }}
                    >
                      <Typography fontWeight={400} fontSize="13px">
                        入眠潜時：30分未満
                      </Typography>
                      <Typography fontWeight={400} fontSize="13px">
                        中途覚醒時間：1時間未満
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderRight: "solid 1px #D8D7D6" }}>
                      <Typography fontWeight={400} fontSize="13px">
                        85％以上
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ReportDailyResultImg
                        type={3}
                        style={{ margin: "auto", display: "block" }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ borderRight: "solid 1px #D8D7D6" }}>
                      <Typography fontWeight={400} fontSize="13px">
                        85％未満
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ReportDailyResultImg
                        type={2}
                        style={{ margin: "auto", display: "block" }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ borderRight: "dashed 1px #D8D7D6" }}>
                      <Typography fontWeight={400} fontSize="13px">
                        入眠潜時：30分以上
                      </Typography>
                      <Typography fontWeight={400} fontSize="13px">
                        中途覚醒時間：1時間以上
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderRight: "solid 1px #D8D7D6" }}>
                      <Typography fontWeight={400} fontSize="13px">
                        （不問）
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ReportDailyResultImg
                        type={1}
                        style={{ margin: "auto", display: "block" }}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
          <Stack>
            <WrappedSection title="用語解説" color="#4D89BD">
              <List
                sx={{
                  py: 0,
                  pl: 5,
                  listStyleType: "disc",
                  "& .MuiListItem-root": {
                    display: "list-item",
                    "&::marker": {
                      content: "'・ '",
                    },
                  },
                }}
              >
                <ListItem disablePadding>
                  <ListItemText>
                    <Typography variant="caption">
                      睡眠効率：寝床にいた時間のうち、実際に眠っていた時間の割合
                    </Typography>
                  </ListItemText>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText>
                    <Typography variant="caption">
                      入眠潜時：寝付くまでの時間
                    </Typography>
                  </ListItemText>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText>
                    <Typography variant="caption">
                      中途覚醒時間：睡眠中の覚醒時間
                    </Typography>
                  </ListItemText>
                </ListItem>
              </List>
            </WrappedSection>
          </Stack>
        </Stack>
      </Stack>
      <Stack sx={{ pb: 6 }}>
        <SubtitleTypography>睡眠の質判定</SubtitleTypography>
      </Stack>
      <Stack sx={{ pb: 12 }}>
        <RiskLabel
          type={SLEEP_JUDGE_TITLE_CONFIG[data.result]}
          title={data.result}
          summary={data.summary}
        />
      </Stack>
      <Divider />
      <Stack>
        <Typography variant="h6" sx={{ color: "text.secondary", pt: 8 }}>
          あわせて読みたい
        </Typography>

        <ReportNavigate
          navigates={[
            {
              link: `/${userType}/report/${uuid}/quantity`,
              text: "睡眠の長さ",
            },
            {
              link: `/${userType}/report/${uuid}/rhythm`,
              text: "睡眠のリズム",
            },
          ]}
          reportUid={uuid}
        />
      </Stack>
    </Stack>
  );
}

function QualityTable({ data }: { data: Quality2Response }) {
  return (
    <ScrollableContainer>
      <TableContainer
        sx={{
          minWidth: "768px",
          borderRadius: 0,
          border: "solid 1px #D8D7D6",
          mb: 4,
          ml: 6,
          mr: 6,
          "& thead tr th, tr tbody td": {
            whiteSpace: "nowrap",
          },
          "& tbody tr td": {
            py: 4,
            px: 0,
            border: "none",
            "&:nth-of-type(1)": {
              px: 4,
              borderRight: "solid 1px #D8D7D6",
            },
            "&:not(:nth-of-type(-n + 1))": {
              textAlign: "center",
            },
          },
          "& tbody tr:nth-of-type(3) td": {
            borderBottom: "3px double #D8D7D6",
          },
          "& thead tr th": {
            p: "8px 0",
            "&:not(:nth-of-type(1))": {
              width: "83.71px",
              "& .MuiTypography-caption": {
                display: "block",
                textAlign: "center",
              },
            },
            "&:nth-of-type(1)": {
              width: "120px",
              borderRight: "solid 1px #D8D7D6",
              px: "16px",
              "& .MuiTypography-caption": {
                display: "block",
                textAlign: "left",
              },
            },
            "&:not(:nth-of-type(-n + 2))": {
              "& .MuiTypography-caption": {
                borderLeft: "solid 1px #D8D7D6",
              },
            },
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="caption">項目</Typography>
              </TableCell>
              {Array.from({ length: 7 }).map((_, i) => (
                <TableCell key={i}>
                  <Typography variant="caption">{i + 1}日目</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography fontWeight={400} fontSize="13px">
                  睡眠効率
                </Typography>
              </TableCell>
              {data.measurement_result.sleep_efficiencies?.map((x, i) => (
                <TableCell key={i} sx={{ textAlign: "center" }}>
                  <Typography fontWeight={400} fontSize="13px">
                    {x?.value != null ? `${x.value}％` : "-"}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography fontWeight={400} fontSize="13px">
                  入眠潜時
                </Typography>
              </TableCell>
              {data.measurement_result.sleep_onset_latency?.map((x, i) => (
                <TableCell key={i} sx={{ textAlign: "center" }}>
                  <Typography fontWeight={400} fontSize="13px">
                    {x?.value != null ? formatDisplayTime(x.value * 60) : "-"}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography fontWeight={400} fontSize="13px">
                  中途覚醒時間
                </Typography>
              </TableCell>
              {data.measurement_result.waso?.map((x, i) => (
                <TableCell key={i}>
                  <Typography fontWeight={400} fontSize="13px">
                    {x?.value != null
                      ? `${formatDisplayTime(x.value / 60)}`
                      : "-"}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography fontWeight={400} fontSize="13px">
                  日別判定
                </Typography>
              </TableCell>
              {data.each_day_sleep?.map((x, i) => (
                <TableCell key={i} sx={{ textAlign: "center" }}>
                  {x != null ? (
                    <ReportDailyResultImg
                      type={x}
                      style={{ margin: "auto", display: "block" }}
                    />
                  ) : (
                    <Typography fontWeight={400} fontSize="13px">
                      -
                    </Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </ScrollableContainer>
  );
}

function SleepEfficiencyChart(props: {
  data: {
    // 測定日
    date: string;
    // 睡眠の効率
    value: number | null;
  }[];
}) {
  const theme = useTheme();
  // minは、チャートの縦軸の下限値。測定データが空の場合は0とする。
  let min = 0;
  if (props.data.some((d) => d.value != null)) {
    const minValue = Math.min(...props.data.map((d) => d.value ?? 100));
    if (80 <= minValue) {
      min = 70;
    } else if (70 <= minValue) {
      min = 60;
    } else if (60 <= minValue) {
      min = 50;
    } else {
      min = 0;
    }
  }

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const options: Highcharts.Options = {
    chart: {
      type: "column",
      height: 300,
      marginLeft: 36,
      marginRight: 0,
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: props.data.map((d) => {
        const date = parse(d.date, "yyyy-MM-dd", new Date());
        return `${format(date, "M/d")}<br>${"日月火水木金土"[date.getDay()]}`;
      }),
      opposite: true,
      lineWidth: 0,
      labels: {
        rotation: 0,
        style: {
          color: theme.palette.text.secondary,
          fontSize: "10px",
        },
      },
    },
    tooltip: {
      enabled: false,
    },
    yAxis: {
      title: {
        text: "",
      },
      labels: {
        enabled: false,
      },
      max: 100,
      min: min,
      tickInterval: 5,
      tickPositions: [min, 85, 100],
      plotBands: [
        {
          color: "#e0f5f3",
          from: 85,
          to: 100,
        },
      ],
      plotLines: [
        {
          value: min,
          zIndex: 2,
          width: 0,
          label: {
            x: -30, // グラフ左端と8pxの余白
            y: 5,
            text: `${min}%`,
            style: {
              color: theme.palette.text.secondary,
              fontFamily: "Noto Sans JP",
              fontSize: "10px",
              fontWeight: "500",
            },
          },
        },
        {
          color: "#00a99d",
          value: 85,
          zIndex: 2,
          width: 2,
          label: {
            x: -30, // グラフ左端と8pxの余白
            y: 5,
            text: "85%",
            style: {
              color: "#00A99D",
              fontFamily: "Noto Sans JP",
              fontSize: "10px",
              fontWeight: "500",
            },
          },
        },
        {
          value: 100,
          zIndex: 2,
          width: 0,
          label: {
            x: -30, // グラフ左端と8pxの余白
            y: 5,
            text: "100%",
            style: {
              color: theme.palette.text.secondary,
              fontFamily: "Noto Sans JP",
              fontSize: "10px",
              fontWeight: "500",
            },
          },
        },
      ],
    },
    legend: {
      align: "right",
      verticalAlign: "top",
      symbolHeight: 10,
      symbolRadius: 0,
      itemStyle: {
        color: theme.palette.text.secondary,
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderRadius: 0,
      },
    },
    series: [
      {
        borderWidth: 0,
        type: "column",
        color: "#003C70",
        showInLegend: false,
        data: props.data.map((d) => d.value),
        dataLabels: {
          enabled: false,
        },
        stack: 0,
        events: {
          legendItemClick(e: SeriesLegendItemClickEventObject) {
            e.preventDefault();
          },
        },
        zIndex: 4,
      },
      // NOTE: 「特徴があった日」をlegendに表示する為に、dataがundefinedの以下SeriesOptionを追加している
      //        legend内の「特徴があった日」のsymbolを円にする為、scatter typeにしている
      {
        type: "scatter",
        marker: {
          radius: 5,
        },
        color: theme.palette.error.main,
        showInLegend: false,
        data: undefined,
        stack: 0,
        events: {
          legendItemClick(e: SeriesLegendItemClickEventObject) {
            e.preventDefault();
          },
        },
      },
    ],
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      ref={chartComponentRef}
    />
  );
}
function SOLandWASOChart(props: {
  data: {
    // 測定日
    date: string;
    // 睡眠の効率
    value: number | null;
  }[];
  threshold: number;
}) {
  const theme = useTheme();
  const max = Math.max(
    ...[...props.data.map((d) => d.value).filter(isNotNullish), 120] // データの最大値が120分以下の場合、最大値を120分とする。
  );
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const options: Highcharts.Options = {
    chart: {
      type: "column",
      height: 300,
      marginLeft: 36,
      marginRight: 0,
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: props.data.map((d) => {
        const date = parse(d.date, "yyyy-MM-dd", new Date());
        return `${format(date, "M/d")}<br>${"日月火水木金土"[date.getDay()]}`;
      }),
      opposite: true,
      lineWidth: 0,
      labels: {
        rotation: 0,
        style: {
          color: theme.palette.text.secondary,
          fontSize: "10px",
        },
      },
    },
    tooltip: {
      enabled: false,
    },
    yAxis: {
      title: {
        text: "",
      },
      labels: {
        enabled: false,
      },
      max,
      tickInterval: 5,
      plotBands: [
        {
          color: "#e0f5f3",
          from: 0,
          to: props.threshold,
        },
      ],
      plotLines: [
        {
          // plotBand(0〜threshold)と同じ zIndex(0) だと band が基準線の下半分を
          // 上から塗りつぶし、線が細く見える不具合になる。band より前面に出す。
          zIndex: 2,
          color: "#00a99d",
          label: {
            x: -30, // グラフ左端と8pxの余白
            y: 5,
            text: `${props.threshold}分`,
            style: {
              color: "#00A99D",
              fontFamily: "Noto Sans JP",
              fontSize: "10px",
              fontWeight: "500",
            },
          },
          value: props.threshold,
          width: 2,
        },
        {
          color: "#c4c4c4",
          value: 0,
          width: 1,
        },
      ],
      gridLineWidth: 0,
    },
    legend: {
      align: "right",
      verticalAlign: "top",
      symbolHeight: 10,
      symbolRadius: 0,
      itemStyle: {
        color: theme.palette.text.secondary,
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderRadius: 0,
      },
    },
    series: [
      {
        type: "column",
        color: "#FF3A30",
        showInLegend: false,
        data: props.data.map((d) => d.value ?? 0),
        dataLabels: {
          enabled: false,
        },
        stack: 0,
      },
    ],
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      ref={chartComponentRef}
    />
  );
}
